// src/hooks/useStripeActions.js (v2 – config-driven, uses api)

import { useState, useCallback } from "react";
import { loadStripe } from "@stripe/stripe-js";

import api from "../services/api";
import { API_PATHS } from "../config";

/**
 * Hook for Stripe subscription and Customer Portal actions.
 *
 * @param {Object} options
 * @param {string} options.publishableKey - Stripe publishable key (required)
 * @param {string} [options.priceId] - Default price ID for subscriptions
 * @returns {Object}
 *   - startSubscription({ priceId? }): redirects to Checkout
 *   - openCustomerPortal(): redirects to portal
 *   - loading: boolean
 *   - error: last error (if any)
 */
export default function useStripeActions({
  publishableKey,
  priceId: defaultPriceId,
} = {}) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const startSubscription = useCallback(
    async ({ priceId } = {}) => {
      if (!publishableKey) {
        setError(new Error("Stripe publishable key missing."));
        return;
      }
      const pid = priceId || defaultPriceId;
      if (!pid) {
        setError(new Error("No price ID provided."));
        return;
      }

      setLoading(true);
      setError(null);
      try {
        const { data } = await api.post(API_PATHS.STRIPE_CHECKOUT_SESSION, {
          priceId: pid,
        });

        const stripe = await loadStripe(publishableKey);
        if (!stripe) throw new Error("Failed to load Stripe.js");

        const result = await stripe.redirectToCheckout({
          sessionId: data.sessionId,
        });

        if (result.error) throw result.error;
      } catch (e) {
        console.error("startSubscription error:", e);
        setError(e);
      } finally {
        setLoading(false);
      }
    },
    [publishableKey, defaultPriceId]
  );

  const openCustomerPortal = useCallback(async () => {
    if (!publishableKey) {
      setError(new Error("Stripe publishable key missing."));
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const { data } = await api.post(API_PATHS.STRIPE_PORTAL_SESSION);

      if (data?.url) {
        window.location.href = data.url;
      } else {
        throw new Error("Portal URL missing from response.");
      }
    } catch (e) {
      console.error("openCustomerPortal error:", e);
      setError(e);
    } finally {
      setLoading(false);
    }
  }, [publishableKey]);

  return { startSubscription, openCustomerPortal, loading, error };
}